import {
  OrganizationUserAccessTypes,
  OrganizationUserEntityRelationTypes,
} from '@generated/graphql';
import type { IUserEntity } from '@ovComponents/3-pattern/viewAllProfilesDialog/viewAllProfilesDialog.types';
import { MemberTag } from '@pages/HouseholdDetail/memberTag';
import { UserContext } from '@providers/userContextProvider';
import { entityInitials, entityName } from '@utils/common';
import { Avatar, Box, MenuItem, Typography } from 'hephaestus/web';
import { CheckRounded as CheckRoundedIcon } from 'hephaestus/webIcons';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useEntityMenuColors } from './useEntityMenuColors';

export type EntityListItemProp = {
  item: IUserEntity;
  isActive: boolean;
  index: number;
  onClick: () => void;
};

export const EntityListItem = ({
  item,
  isActive,
  index,
  onClick,
}: EntityListItemProp) => {
  const { userContext } = useContext(UserContext);
  const { t } = useTranslation(['entityMenu']);
  const colors = useEntityMenuColors();

  const profileName = useMemo(
    () => entityName(item?.entity) || item?.entity?.primaryEmail,
    [item?.entity],
  );
  const profileInitials = useMemo(
    () => entityInitials(item?.entity),
    [item?.entity],
  );

  const isAccessGranted =
    userContext.accessType === OrganizationUserAccessTypes.RepCode ||
    item?.relation !== OrganizationUserEntityRelationTypes.Owner;
  const colorIndex = index % colors.length;

  return (
    <MenuItem
      data-testid={`profile-${index}`}
      sx={{
        px: 1.5,
        py: 0,
        height: '64px',
        display: 'flex',
        justifyContent: 'space-between',
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Avatar
          selected={isActive}
          size='small'
          sx={{
            color: colors[colorIndex].color,
            backgroundColor: colors[colorIndex].colorsInner,
          }}
        >
          {profileInitials}
        </Avatar>
        <Box>
          <Typography
            align='left'
            variant='bodyMedium'
            sx={{
              fontWeight: isActive ? 600 : 500,
              maxWidth: '140px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {profileName}
          </Typography>
          {isAccessGranted && (
            <MemberTag
              sequenceNumber={colorIndex}
              name={t('entityMenu:accessGranted')}
            />
          )}
        </Box>
      </Box>
      {isActive && <CheckRoundedIcon />}
    </MenuItem>
  );
};
